import type { QueryClient } from '@tanstack/react-query';
import { segmentKeys, type Segment } from './entity';

/**
 * Imperative segment cache ops for the mutation hooks. The bulk archive/delete
 * endpoints return 204 (no body) and there's no identity map, so mutations must
 * patch the cache themselves: flip `archived` on the affected detail slots and
 * invalidate the (always-fresh) lists.
 */

export function setSegmentCache(qc: QueryClient, workspaceId: number, segment: Segment): void {
  qc.setQueryData(segmentKeys.detail(workspaceId, segment.id), segment);
}

export function getSegmentCache(qc: QueryClient, workspaceId: number, id: string): Segment | undefined {
  return qc.getQueryData<Segment>(segmentKeys.detail(workspaceId, id));
}

export function removeSegmentCache(qc: QueryClient, workspaceId: number, id: string): void {
  // Drop the detail slot and every per-id derived read (count/metrics/status/usage)
  // so a deleted segment leaves nothing stale behind.
  for (const key of [
    segmentKeys.detail(workspaceId, id),
    segmentKeys.count(workspaceId, id),
    segmentKeys.metrics(workspaceId, id),
    segmentKeys.status(workspaceId, id),
    segmentKeys.usage(workspaceId, id),
  ]) {
    qc.removeQueries({ queryKey: key });
  }
}

export function invalidateSegmentLists(qc: QueryClient, workspaceId: number): void {
  void qc.invalidateQueries({ queryKey: segmentKeys.lists(workspaceId) });
}

/**
 * Drop every bulk-usage entry for the workspace. Bulk usage is keyed by the id
 * *set* (`usageBulk`), not per id, so `removeSegmentCache` (per id) can't reach
 * entries that included a now-deleted segment, and `staleTime: 0` won't refetch
 * an already-mounted bulk query. Remove the subtree so the next read rebuilds it
 * for the surviving set.
 */
export function removeBulkUsageCache(qc: QueryClient, workspaceId: number): void {
  qc.removeQueries({ queryKey: [...segmentKeys.all(workspaceId), 'usage', 'bulk'] });
}

/** Flip `archived` on each cached detail slot (bulk archive/unarchive return no body). */
export function patchSegmentArchived(
  qc: QueryClient,
  workspaceId: number,
  ids: readonly string[],
  archived: boolean,
): void {
  for (const id of ids) {
    const current = getSegmentCache(qc, workspaceId, id);
    if (current != null) {
      qc.setQueryData(segmentKeys.detail(workspaceId, id), { ...current, archived });
    }
  }
}
